import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { QRCodeCanvas } from "qrcode.react";

import BreadCrumb from "Components/Common/BreadCrumb";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { decodeTicketToken } from "helpers/fakebackend_helper";
import { fetchTopRecentIssuancePlans } from "helpers/issuancePlanConfigLookup";
import { decodeScannedTicket } from "helpers/ticketQrDecode";
import { fetchBrandingConfig } from "slices/thunks";
import { setConfig as setBrandingConfig } from "slices/brandingConfiguration/reducer";
import { loadSystemConfigWithCache } from "helpers/systemConfigCache";
import { CacheService } from "services/cache";
import { CacheConfig } from "services/cache/CacheConfig";
import {
  DrawResultPrize,
  fetchDrawResultFile,
  isTicketMatchPrize,
} from "helpers/drawResultLookup";
import "./lookup-portal.scss";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('lookup_portal');

type PrizeCheckStatus = "won" | "not-won" | "no-data";

interface PrizeCheckResult {
  status: PrizeCheckStatus;
  matchedPrizes?: DrawResultPrize[];
}

const DEFAULT_PRIMARY_COLOR = "#1b3a5c";
const DEFAULT_SECONDARY_COLOR = "#0f2a4a";
const DEFAULT_COMPANY_NAME = st('xo_so_kien_thiet_thu_do_text');

interface LookupPeriodOption {
  id: number;
  planCode: string;
  expectedDrawDate: string;
  status?: number;
  numberRangeStart?: string | null;
  numberRangeEnd?: string | null;
  ticketSymbols?: string[];
}

const DEFAULT_DIGIT_COUNT = 6;

const formatDate = (value?: string | null) =>
  value ? moment(value).format("DD/MM/YYYY") : "—";

const LookupPortalPage: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const [searchParams] = useSearchParams();
  const prefillTokenRef = useRef<string | null>(searchParams.get("token"));

  // Đọc qua Redux (không đọc thẳng cache 1 lần lúc mount) vì cache SystemConfig/Branding có TTL
  // ngắn (30 phút / 5 phút) — nếu chỉ "peek" cache như trước, hễ cache hết hạn là rơi về mặc định
  // vĩnh viễn cho tới khi có trang khác tình cờ fetch lại. loadSystemConfigWithCache và branding
  // loader dưới đây tự fetch API khi cache miss/hết hạn, giống hệt cách IssuancePlan/Header đang làm.
  const systemConfig = useSelector(
    (state: any) => state.SystemConfiguration?.config,
  );
  const brandingConfig = useSelector(
    (state: any) => state.BrandingConfiguration?.config,
  );

  const [periods, setPeriods] = useState<LookupPeriodOption[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);

  const [ticketNumber, setTicketNumber] = useState("");
  const [symbol, setSymbol] = useState("");
  const [periodId, setPeriodId] = useState("");

  const [qrValue, setQrValue] = useState<string | null>(null);
  const [qrCaption, setQrCaption] = useState(
    st('quet_ma_qr_bang_dien_thoai_text'),
  );

  // Ket qua check trung thuong — doc truc tiep file cong khai tren MinIO theo dung ky dang chon,
  // doc lap voi luong tao QR tra cuu o tren (khong thay the, chi bo sung).
  const [prizeCheckLoading, setPrizeCheckLoading] = useState(false);
  const [prizeCheckResult, setPrizeCheckResult] =
    useState<PrizeCheckResult | null>(null);

  // Tu-dien form khi man duoc mo tu 1 ve cu the (query "?token=", xem SearchByPrizeModal) — token
  // duoc giai ma o backend ra ky/so ve/ky hieu, roi fill vao dung cac state da co san ben duoi
  // (khong doi logic nhap tay hien tai). pendingPrefill giu tam so ve/ky hieu cho toi khi effect
  // reset-theo-ky (o duoi) chay xong, tranh bi ghi de ve rong.
  const [pendingPrefill, setPendingPrefill] = useState<{
    ticketNumber: string;
    symbol: string;
  } | null>(null);
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);

  useEffect(() => {
    loadSystemConfigWithCache(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const loadBrandingConfig = async () => {
      const cacheKey = CacheConfig.BRANDING.key;
      const cacheTtl = CacheConfig.BRANDING.ttl;
      const cached = CacheService.get(cacheKey);

      if (cached.cached && cached.data) {
        dispatch(setBrandingConfig(cached.data));
      } else {
        const result: any = await dispatch(fetchBrandingConfig());
        if (result.payload) {
          CacheService.set(cacheKey, result.payload, cacheTtl);
        }
      }
    };

    loadBrandingConfig();
  }, [dispatch]);

  const digitCount = systemConfig?.digitCount || DEFAULT_DIGIT_COUNT;
  const lookupBaseUrl = (
    systemConfig?.lookupBaseUrl || window.location.origin
  ).replace(/\/+$/, "");

  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const items = await fetchTopRecentIssuancePlans();
        const list: LookupPeriodOption[] = items;
        setPeriods(list);
        if (list.length > 0 && !prefillTokenRef.current) {
          setPeriodId(String(list[0].id));
        }
      } catch {
        toast.error(
          st('khong_tai_duoc_danh_sach_ky_message'),
        );
      } finally {
        setLoadingPeriods(false);
      }
    };

    loadPeriods();
  }, []);

  useEffect(() => {
    const token = prefillTokenRef.current;
    if (!token) return;

    const decodeAndPrefill = async () => {
      try {
        // Dùng chung cách đọc token với màn Giao vé / Thu hồi vé (decodeScannedTicket): ưu tiên xác
        // thực chữ ký OFFLINE bằng public key — hỗ trợ token JWT RS256 lẫn ECDSA nhị phân gọn (định
        // dạng vé hiện tại), không cần quyền "ticketlookup.decode"; chỉ gọi API /decode khi token là
        // định dạng AES-256-GCM cũ.
        const decoded = await decodeScannedTicket(token, decodeTicketToken);

        if (
          !decoded ||
          !decoded.lotteryPeriodId ||
          !decoded.ticketNumber ||
          !decoded.symbol
        ) {
          toast.error(st('khong_doc_duoc_thong_tin_ve_message'));
          return;
        }

        setPendingPrefill({
          ticketNumber: decoded.ticketNumber,
          symbol: decoded.symbol,
        });
        setPeriodId(String(decoded.lotteryPeriodId));
      } catch (err: any) {
        toast.error(
          err?.data?.message ||
            err?.message ||
            st('khong_the_doc_thong_tin_ve_message'),
        );
      }
    };

    decodeAndPrefill();
  }, []);

  const selectedPeriod = useMemo(
    () => periods.find((p) => String(p.id) === periodId),
    [periods, periodId],
  );
  const effectiveDigitCount =
    selectedPeriod?.numberRangeStart?.length || digitCount;
  const availableSymbols = selectedPeriod?.ticketSymbols || [];

  useEffect(() => {
    setTicketNumber("");
    setSymbol((prevSymbol) =>
      availableSymbols.includes(prevSymbol)
        ? prevSymbol
        : availableSymbols[0] || "",
    );
    setPrizeCheckResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId]);

  useEffect(() => {
    if (!pendingPrefill) return;

    setTicketNumber(pendingPrefill.ticketNumber);
    setSymbol(pendingPrefill.symbol);
    setPendingPrefill(null);
    setAutoSubmitPending(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId]);

  const handleTicketNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTicketNumber(
      e.target.value.replace(/\D/g, "").slice(0, effectiveDigitCount),
    );
  };

  const checkWinningPrize = useCallback(
    async (currentPeriodId: string, currentTicketNumber: string) => {
      setPrizeCheckLoading(true);
      setPrizeCheckResult(null);
      try {
        const drawResult = await fetchDrawResultFile(currentPeriodId);
        if (
          !drawResult ||
          Number(drawResult.issuancePlanId) !== Number(currentPeriodId)
        ) {
          setPrizeCheckResult({ status: "no-data" });
          return;
        }

        const matchedPrizes = drawResult.prizes.filter((prize) =>
          isTicketMatchPrize(currentTicketNumber, prize),
        );
        setPrizeCheckResult(
          matchedPrizes.length > 0
            ? { status: "won", matchedPrizes }
            : { status: "not-won" },
        );
      } catch {
        setPrizeCheckResult({ status: "no-data" });
      } finally {
        setPrizeCheckLoading(false);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!ticketNumber || ticketNumber.length !== effectiveDigitCount) {
        toast.error(st('vui_long_nhap_du_effective_digit_message', { effective_digit_count: effectiveDigitCount }));
        return;
      }
      if (!symbol.trim()) {
        toast.error(st('vui_long_chon_ky_hieu_ve_message'));
        return;
      }
      if (!periodId) {
        toast.error(st('vui_long_chon_ky_mo_thuong_message'));
        return;
      }

      await checkWinningPrize(periodId, ticketNumber);
    },
    [ticketNumber, symbol, periodId, effectiveDigitCount, checkWinningPrize],
  );

  useEffect(() => {
    if (!autoSubmitPending) return;
    if (!ticketNumber || !symbol || !periodId) return;

    setAutoSubmitPending(false);
    handleSubmit({ preventDefault: () => undefined } as React.FormEvent);
  }, [autoSubmitPending, ticketNumber, symbol, periodId, handleSubmit]);

  const portalUrl = useMemo(
    () => `${lookupBaseUrl}/tra-cuu-ket-qua`,
    [lookupBaseUrl],
  );
  const displayedQrValue = qrValue || portalUrl;

  const handleDownloadQr = () => {
    const canvas = document.getElementById(
      "lp-qr-canvas",
    ) as HTMLCanvasElement | null;
    if (!canvas) return;

    const link = document.createElement("a");
    link.download = "qr-tra-cuu-ket-qua.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handlePrintQr = () => window.print();

  const companyName = brandingConfig?.companyName || DEFAULT_COMPANY_NAME;
  const primaryColor = brandingConfig?.primaryColor || DEFAULT_PRIMARY_COLOR;
  const secondaryColor =
    brandingConfig?.secondaryColor || DEFAULT_SECONDARY_COLOR;
  const brandingStyle = {
    "--lp-primary": primaryColor,
    "--lp-secondary": secondaryColor,
  } as React.CSSProperties;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('cong_tra_cuu_ket_qua_title')} pageTitle={st('tra_cuu_ve_so_text')} />

          <div className="lookup-portal-page" style={brandingStyle}>
            <div className="lp-container">
              {/* Card 1 — Header + Form tra cứu */}
              <section className="lp-card">
                <div className="lp-header-banner">
                  {brandingConfig?.logoUrl ? (
                    <img
                      src={brandingConfig.logoUrl}
                      alt={companyName}
                      className="lp-logo-img"
                    />
                  ) : (
                    <div className="lp-logo">
                      {brandingConfig?.companyShortName || "XSKTD"}
                    </div>
                  )}
                  <h1 className="lp-title">{st('cong_tra_cuu_ket_qua_title_2')}</h1>
                  <p className="lp-subtitle">
                    {companyName} {st('danh_cho_khach_hang_text')}
                  </p>
                </div>

                <form className="lp-form-body" onSubmit={handleSubmit}>
                  <div className="lp-form-row">
                    <div className="lp-form-field">
                      <label
                        className="lp-label"
                        htmlFor="lp-ticket-number"
                      >{st('nhap_so_ve_effective_digit_count_text', { effective_digit_count: effectiveDigitCount })}</label>
                      <input
                        id="lp-ticket-number"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className="lp-input"
                        placeholder={"0".repeat(effectiveDigitCount)}
                        value={ticketNumber}
                        data-action-code="input-ticket-number"
                        data-action-name="Số vé"
                        onChange={handleTicketNumberChange}
                        maxLength={effectiveDigitCount}
                        disabled={!PageActionPermission.canEnable(location.pathname, "input-ticket-number")}
                      />
                    </div>
                    <div className="lp-form-field">
                      <label className="lp-label" htmlFor="lp-symbol">
                        {st('ky_hieu_ve_label')}
                      </label>
                      {availableSymbols.length > 0 ? (
                        <select
                          id="lp-symbol"
                          className="lp-select lp-input-symbol"
                          value={symbol}
                          data-action-code="input-symbol"
                          data-action-name="Ký hiệu vé"
                          onChange={(e) => setSymbol(e.target.value)}
                          disabled={!PageActionPermission.canEnable(location.pathname, "input-symbol")}
                        >
                          {availableSymbols.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id="lp-symbol"
                          type="text"
                          autoComplete="off"
                          className="lp-input lp-input-symbol"
                          placeholder={st('vd_01a_placeholder')}
                          value={symbol}
                          data-action-code="input-symbol"
                          data-action-name="Ký hiệu vé"
                          onChange={(e) =>
                            setSymbol(e.target.value.toUpperCase())
                          }
                          maxLength={10}
                          disabled={!PageActionPermission.canEnable(location.pathname, "input-symbol")}
                        />
                      )}
                    </div>
                  </div>

                  <div className="lp-form-field mb-4">
                    <label className="lp-label" htmlFor="lp-period">
                      {st('ky_mo_thuong_label')}
                    </label>
                    <select
                      id="lp-period"
                      className="lp-select"
                      value={periodId}
                      data-action-code="input-period"
                      data-action-name="Kỳ mở thưởng"
                      onChange={(e) => setPeriodId(e.target.value)}
                      disabled={
                        loadingPeriods ||
                        periods.length === 0 ||
                        !PageActionPermission.canEnable(location.pathname, "input-period")
                      }
                    >
                      {loadingPeriods && <option value="">{st('dang_tai_option')}</option>}
                      {!loadingPeriods && periods.length === 0 && (
                        <option value="">{st('khong_co_ky_mo_thuong_option')}</option>
                      )}
                      {periods.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.planCode} — {formatDate(p.expectedDrawDate)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {PageActionPermission.canDisplay(location.pathname, "button-search") && (
                    <button
                      type="submit"
                      className="lp-cta-btn"
                      data-action-code="button-search"
                      data-action-name="Tra cứu kết quả"
                      disabled={
                        prizeCheckLoading ||
                        loadingPeriods ||
                        periods.length === 0
                      }
                    >
                      <i className="ri-search-line"></i>
                      {prizeCheckLoading
                        ? st('dang_tra_cuu_text')
                        : st('tra_cuu_ket_qua_ngay_text')}
                    </button>
                  )}

                  {(prizeCheckLoading || prizeCheckResult) && selectedPeriod && (
                    <div className="lp-result-drawdate">
                      <i className="ri-calendar-event-line"></i>
                      {st('ngay_mo_thuong_text')}{" "}
                      <strong>{formatDate(selectedPeriod.expectedDrawDate)}</strong>
                    </div>
                  )}

                  {(prizeCheckLoading || prizeCheckResult) && (
                    <div
                      className={`lp-prize-result ${prizeCheckResult?.status === "won" ? "lp-prize-result-won" : ""}`}
                    >
                      {prizeCheckLoading ? (
                        <>
                          <i className="ri-loader-4-line"></i>
                          <span>{st('dang_kiem_tra_ket_qua_trung_text')}</span>
                        </>
                      ) : prizeCheckResult?.status === "won" ? (
                        <>
                          <i className="ri-trophy-line"></i>
                          <div>
                            <strong>{st('chuc_mung_ve_trung_thuong_text')}</strong>
                            <ul className="lp-prize-result-list">
                              {prizeCheckResult.matchedPrizes?.map((prize) => (
                                <li key={prize.prizeTierCode}>
                                  {prize.displayName}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      ) : prizeCheckResult?.status === "not-won" ? (
                        <>
                          <i className="ri-information-line"></i>
                          <span>{st('rat_tiec_ve_khong_trung_thuong_text')}</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-information-line"></i>
                          <span>
                            {st('chua_cap_nhat_thong_tin_tra_text')}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </form>
              </section>

              {/* Card 2 — Quét mã QR để tra cứu nhanh */}
              <section className="lp-card lp-qr-card">
                <div className="lp-section-header">
                  <i className="ri-qr-code-line"></i>
                  <span>{st('quet_ma_qr_de_tra_cuu_text')}</span>
                </div>
                <div className="lp-qr-body">
                  <div className="lp-qr-frame">
                    <QRCodeCanvas
                      id="lp-qr-canvas"
                      value={displayedQrValue}
                      size={168}
                    />
                  </div>
                  <p className="lp-qr-caption">{qrCaption}</p>
                  <div className="lp-secondary-buttons">
                    {PageActionPermission.canDisplay(location.pathname, "button-download-qr") && (
                      <button
                        type="button"
                        className="lp-btn-outline"
                        data-action-code="button-download-qr"
                        data-action-name="Tải QR"
                        onClick={handleDownloadQr}
                      >
                        <i className="ri-download-2-line align-bottom me-1"></i>
                        {st('tai_qr_button')}
                      </button>
                    )}
                    {PageActionPermission.canDisplay(location.pathname, "button-print-qr") && (
                      <button
                        type="button"
                        className="lp-btn-outline"
                        data-action-code="button-print-qr"
                        data-action-name="In QR"
                        onClick={handlePrintQr}
                      >
                        <i className="ri-printer-line align-bottom me-1"></i>
                        {st('in_qr_button')}
                      </button>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LookupPortalPage;
